import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useParams } from "react-router-dom";
import { decodeEntities } from "../../utils/decodeEntities";
import {
  BookOpenIcon,
  PencilSquareIcon,
  DocumentTextIcon,
  SpeakerWaveIcon,
  PlusCircleIcon,
  XMarkIcon,
  CheckCircleIcon,
  AcademicCapIcon,
  ClockIcon,
  ChartBarIcon,
  ExclamationCircleIcon,
  PaperAirplaneIcon,
  PlayIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import { FaRegCircleStop } from "react-icons/fa6";
import { Section, Card, Button } from "../UI";
import SpeechFeature from "../SpeechFeature";
import SentencePracticeChat from "./SentencePracticeChat";
import { API_BASE_URL, getBackendUrl } from "../../config";
import axios from "axios";
import { getSessionId, identityHeaders } from "../../utils/session";

// Course text arrives as one unbroken block, with the author's examples marked
// "Ex :". This separates the explanation from the examples using only the words
// already present — nothing is invented, reworded or reordered.
const splitLesson = (text) => {
  const clean = decodeEntities(text || "").replace(/\s+/g, " ").trim();
  if (!clean) return { explanation: [], examples: [] };

  const chunks = clean.split(/\bEx\s*:\s*/i);
  const leadParts = [chunks.shift() || ""];
  const examples = [];

  chunks.forEach((chunk) => {
    // A question is a sentence too. Recognising only "." meant every interrogative
    // example was never lifted and landed in the prose instead, so the panel showed the
    // explanation and the examples run together. Match ".", "?" and "!" alike, and keep
    // the mark: dropping it left questions displayed as statements.
    const end = chunk.search(/[.?!](\s|$)/);
    const value = end > 0 ? chunk.slice(0, end + 1).trim() : "";
    const words = value ? value.split(/\s+/).length : 0;
    // Short and clean: an example. Long or unterminated: it is prose that happens to
    // follow an "Ex :", and presenting a paragraph as an example reads as a mistake.
    if (end > 0 && end <= 140 && words > 0 && words <= 12) {
      examples.push(value.charAt(0).toUpperCase() + value.slice(1));
      leadParts.push(chunk.slice(end + 1));
    } else {
      leadParts.push(chunk);
    }
  });

  const lead = leadParts.join(" ").replace(/\s+/g, " ").trim();

  const explanation = [];
  let buffer = "";
  lead
    .split(/\.\s+/)
    .map((sentence) => sentence.trim())
    .filter(Boolean)
    .forEach((sentence) => {
      const piece = /[.!?]$/.test(sentence) ? sentence : `${sentence}.`;
      buffer = buffer ? `${buffer} ${piece}` : piece;
      if (buffer.length > 260) {
        explanation.push(buffer);
        buffer = "";
      }
    });
  if (buffer) explanation.push(buffer);

  return { explanation, examples };
};

// A stage heading, so the four steps are visible as steps.
const StageHeading = ({ number, title, hint }) => (
  <div className="flex items-baseline gap-3">
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">
      {number}
    </span>
    <div>
      <h2 className="text-xl font-bold tracking-tight text-slate-900">{title}</h2>
      {hint && <p className="mt-0.5 text-sm text-slate-500">{hint}</p>}
    </div>
  </div>
);

// A short introduction to the tense: no invented grammar, just that tense's own
// sentences shown one form at a time, in motion.
const TenseIntro = ({ examples }) => {
  const byForm = {};
  (examples || []).forEach((example) => {
    const form = String(example.sentence_type || "").toLowerCase();
    if (["affirmative", "interrogative", "negative"].includes(form) && !byForm[form]) {
      byForm[form] = example;
    }
  });
  const SHAPES = ["affirmative", "interrogative", "negative"];
  const order = SHAPES.filter((form) => byForm[form]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (order.length < 2) return undefined;
    const timer = setInterval(() => setIndex((i) => (i + 1) % order.length), 3800);
    return () => clearInterval(timer);
  }, [order.length]);

  if (order.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center">
        <p className="text-sm text-slate-500">
          Sentences for this lesson will appear here as students add them.
        </p>
        <a href="#practice" className="mt-2 inline-block text-sm font-medium text-orange-600 hover:text-orange-700">
          Be the first to add one
        </a>
      </div>
    );
  }

  const form = order[Math.min(index, order.length - 1)];
  const tone = {
    affirmative: "border-emerald-200 bg-emerald-50 text-emerald-700",
    interrogative: "border-sky-200 bg-sky-50 text-sky-700",
    negative: "border-rose-200 bg-rose-50 text-rose-700",
  }[form];

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div className="flex min-h-[7rem] flex-wrap items-center gap-4 px-5 py-7 sm:px-8">
        <span className={`shrink-0 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide ${tone}`}>
          {form}
        </span>
        <AnimatePresence mode="wait">
          <motion.p
            key={form}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -14 }}
            transition={{ duration: 0.3 }}
            className="text-xl font-medium leading-snug text-slate-900 sm:text-2xl"
          >
            {String(byForm[form].example_text || "")
              .split(" ")
              .map((word, wordIndex) => (
                <motion.span
                  key={`${form}-${wordIndex}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: wordIndex * 0.05, duration: 0.25 }}
                  className="inline-block"
                >
                  {word}&nbsp;
                </motion.span>
              ))}
          </motion.p>
        </AnimatePresence>
      </div>
    </div>
  );
};

const PageCourse = (props) => {
  const [tenseData, setTenseData] = useState({
    tense_id: "",
    title: "",
    content: "",
    grammer_references: "",
    description: "",
    examples: [],
    usage_notes: "",
  });


  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState(false);
  // The route is "/tense/:title" (see App.js), so the parameter is `title`.
  // Reading `tense` always yielded undefined, which made this page request
  // /course/undefined and render "We couldn't find any tense information." — the
  // examples thread and the sentence composer below it never appeared at all.
  const params = useParams();
  const tense = decodeURIComponent(params.title || params.tense || "");
  const [showReferences, setShowReferences] = useState(false);
  const [quizScore, setQuizScore] = useState({
    total_questions: 0,
    correct_answers: 0,
    highestscore: 0,
  });

  // Create a ref for the speech feature
  const speechFeatureRef = React.useRef();

  // --- Speech Synthesis State and Handlers ---
  const [speaking, setSpeaking] = useState(false);
  const msgRef = useRef(null);

  const handleSpeakContent = () => {
    if (msgRef.current) {
      window.speechSynthesis.cancel(); // cancel any ongoing
    }
    // "Listen" would read the entities out loud ("ampersand deg semicolon")
    const utter = new window.SpeechSynthesisUtterance(
      decodeEntities(tenseData.content)
    );
    utter.onstart = () => setSpeaking(true);
    utter.onend = () => setSpeaking(false);
    utter.onerror = () => setSpeaking(false);
    msgRef.current = utter;
    window.speechSynthesis.speak(utter);
  };

  const handleCancelSpeak = () => {
    window.speechSynthesis.cancel();
    setSpeaking(false);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  // New state variables for course stats
  const [courseStats, setCourseStats] = useState({
    completionRate: 0,
    avgScore: 0,
    studyTime: 0,
    totalExamples: 0,
  });

  // Add a new state for tab navigation
  const [activeTab, setActiveTab] = useState("content");

  // where this lesson sits in the course order
  const [lessonNav, setLessonNav] = useState({ previous: null, next: null });

  // Create a ref for scrolling to sections
  const contentRef = useRef(null);
  const examplesRef = useRef(null);

  useEffect(() => {
    // Fetch course data
    fetchtenseData();
    // Scroll to top on component mount
    window.scrollTo(0, 0);
  }, [props.location.pathname]);

  useEffect(() => {
    if (tenseData.tense_id) {
      fetchQuizScore();
      fetchCourseStats();
    }
  }, [tenseData?.tense_id]);

  // the ordered list of lessons, so a reader can move to the next one
  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_API_URL}/tense`)
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (cancelled || !payload) return;
        const list = payload.tense || payload.tenses || payload;
        if (!Array.isArray(list)) return;
        const index = list.findIndex(
          (item) =>
            String(item.title || "").toUpperCase() ===
            String(tenseData?.title || "").toUpperCase()
        );
        if (index === -1) return;
        setLessonNav({
          previous: index > 0 ? list[index - 1] : null,
          next: index < list.length - 1 ? list[index + 1] : null,
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [tenseData?.title]);

  const fetchQuizScore = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/quiz-performance/stats/bytenseid/${tenseData?.tense_id}`,
        {
          headers: {
            Authorization: `${localStorage.getItem("jstoken")}`,
          },
        }
      );

      const performances = await res.data?.performances;
      const bestPerformance = performances.reduce((best, current) => {
        if (current.correct_answers > best.correct_answers) {
          return current;
        } else if (
          current.correct_answers === best.correct_answers &&
          current.avg_time_per_question < best.avg_time_per_question
        ) {
          // If scores are equal, choose the faster one
          return current;
        }
        return best;
      });
      setQuizScore({
        correct_answers: bestPerformance.correct_answers,
        total_questions: bestPerformance.total_questions,
        highestscore:
          (bestPerformance.correct_answers / bestPerformance.total_questions) *
            100 || 0,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const fetchtenseData = async () => {
    setIsLoading(true);
    setErrorMessage("");
    const url = await getBackendUrl();

    // Use the course endpoint with the specific tense
    fetch(`${url}/course/${encodeURIComponent(tense)}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to load course");
        }
        return res.json();
      })
      .then((res) => {
        if (res.courses && res.courses.length > 0) {
          const course = res.courses[0];

          setTenseData({
            tense_id: course.course_id,
            title: course.course_title,
            content: course.course_data,
            grammer_references: course.course_item,
            description: course.course_data, // Use course_data as description
            usage_notes: course.course_item, // Use course_item as usage notes
          });

          setIsLoading(false);
          fetchExamples(course.course_id);
        } else {
          setErrorMessage("No matching course found for this title.");
          setIsLoading(false);
        }
      })

      .catch((err) => {
        console.error("Error fetching course:", err);
        setErrorMessage("Failed to load course. Please try again later.");
        setIsLoading(false);
      });
  };

  const fetchExamples = async (tenseId) => {
    // id 0 is a real tense (PRESENT SIMPLE); `!0` is true and previously skipped
    // loading its examples entirely
    if (tenseId === undefined || tenseId === null || tenseId === "") return;
    setIsLoading(true);

    try {
      const url = await getBackendUrl();
      let backendExamples = [];

      // Get or generate session ID for anonymous users
      let sessionId = localStorage.getItem("session");
      if (!sessionId) {
        sessionId =
          getSessionId();
        localStorage.setItem("session", sessionId);
      }

      // Build headers for fetch
      const headers = {};
      if (localStorage.getItem("jstoken")) {
        const token = localStorage.getItem("jstoken");
        // Check if token already has Bearer prefix to avoid double Bearer
        headers["Authorization"] = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
      } else {
        headers["x-session-id"] = sessionId;
      }

      // The thread is the tense's conversation, not just this visitor's own
      // sentences: the per-session endpoint returns nothing until you have posted
      // yourself, which made a course with plenty of sentences look empty. Fetch
      // the course thread and this visitor's uploads together, then de-duplicate.
      const [courseResponse, mineResponse] = await Promise.all([
        fetch(`${url}/tense/${tenseId}/u`),
        fetch(`${url}/userupload/user/${tenseId}`, { headers }),
      ]);

      const toExample = (upload) => ({
        example_text: upload.sentence || upload.example_text,
        user: {
          user_name:
            upload.user_name ||
            (upload.user && upload.user.user_name) ||
            upload.user_id ||
            "Guest",
        },
        sentence_type: upload.sentence_type || "affirmative",
        created_at: upload.created_at || null,
      });

      let courseExamples = [];
      if (courseResponse.ok) {
        const data = await courseResponse.json();
        const list = Array.isArray(data)
          ? data
          : data.examples || data.userUploads || [];
        courseExamples = list.map(toExample);
      }

      let ownExamples = [];
      if (mineResponse.ok) {
        const res = await mineResponse.json();
        // Accept both array and object for backward compatibility
        const uploads = Array.isArray(res) ? res : res.userUploads || [];
        ownExamples = uploads.map(toExample);
      }

      const seenSentences = new Set();
      backendExamples = [...courseExamples, ...ownExamples].filter((example) => {
        const key = (example.example_text || "").trim().toLowerCase();
        if (!key || seenSentences.has(key)) return false;
        seenSentences.add(key);
        return true;
      });

      // Always check local storage for guest examples (legacy)
      const localData = JSON.parse(
        localStorage.getItem("upload") || '{"upload":[]}'
      );
      const localExamples = localData.upload
        .filter((ex) => ex.tense_id === tenseId)
        .map((ex) => ({
          example_text: ex.sentence,
          user: { user_name: "Guest" },
          sentence_type: ex.sentence_type || "affirmative",
        }));

      // Combine and set examples
      const combinedExamples = [...backendExamples, ...localExamples];
      setTenseData((prev) => ({ ...prev, examples: combinedExamples }));
    } catch (err) {
      console.error("Error fetching examples:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCourseStats = async () => {
    // Simulate fetching course stats from the server
    try {
      const res = await axios.get(
        `${API_BASE_URL}/tense/${tenseData?.tense_id}/stats`,
        {
          headers: {
            Authorization: localStorage.getItem("jstoken"),
          },
        }
      );
      const stats = res.data.stats;
      setCourseStats({
        completionRate: stats.completion_percentage || 0,
        avgScore: stats.average_score || 0,
        // study time used to be Math.floor(1 + Math.random() * 2): a made-up number
        // shown as if it were measured. Better to show nothing than a fiction.
        studyTime: null,
        totalExamples: stats.examples.total || 0,
      });
    } catch (error) {
      console.log(error);
    }
  };

  // Restored: the "listen to your examples" button, which my edit above cut away
  // with the old upload form.
  const speakExamples = () => {
    if (speechFeatureRef.current && tenseData.examples?.length > 0) {
      const mapped = tenseData.examples.map((eg) => {
        return eg.example_text;
      });
      speechFeatureRef.current.speak(mapped.join(". "));
    }
  };

  const looksLikeASentence = (text) => {
    const value = String(text || "").trim();
    if (value.length < 2) return false;
    return /\s/.test(value) || value.length <= 12;
  };

  // what a reader needs out of the one-block course text
  const lesson = splitLesson(tenseData?.content || tenseData?.description);
  const hasDistinctContent =
    Boolean(tenseData?.content) &&
    decodeEntities(tenseData.content).trim() !==
      decodeEntities(tenseData.description).trim();
  const lessonText = [
    tenseData?.title,
    decodeEntities(tenseData?.description),
    ...(tenseData?.examples || []).map((example) => example.example_text),
  ]
    .filter(Boolean)
    .join(". ");

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Success Message */}
      {uploadSuccess && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="fixed top-20 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center"
        >
          <CheckCircleIcon className="w-5 h-5 mr-2" />
          <p>Example added successfully!</p>
        </motion.div>
      )}

      {/* Course Header */}
      {/* <Section className="pt-20 pb-12 bg-gradient-to-r from-orange-600 to-red-600"> */}
        {/* <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="container mx-auto px-4"
        > */}
          {/* <div className="max-w-4xl mx-auto text-center"> */}
            {/* <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {tenseData?.title.toUpperCase()}
            </h1>
            <div className="w-20 h-1 bg-white mx-auto mb-4"></div>
            <p className="text-orange-100 max-w-2xl mx-auto mb-8">
              Master your language skills with comprehensive tense materials and
              personalized examples
            </p> */}

            {/* Navigation Tabs */}
            {/* <div className="flex justify-center mt-8 space-x-2 md:space-x-4">
              <button
                onClick={() => {
                  setActiveTab("content");
                  setTimeout(() => scrollToSection(contentRef), 100);
                }}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === "content"
                    ? "bg-white text-orange-600"
                    : "bg-white/20 text-white hover:bg-white/30"
                }`}
              >
                <BookOpenIcon className="w-5 h-5 inline mr-1" /> Tense Content
              </button>

              <button
                onClick={() => {
                  setActiveTab("examples");
                  setTimeout(() => scrollToSection(examplesRef), 100);
                }}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === "examples"
                    ? "bg-white text-orange-600"
                    : "bg-white/20 text-white hover:bg-white/30"
                }`}
              >
                <DocumentTextIcon className="w-5 h-5 inline mr-1" /> Your
                Examples
              </button>

              <Link
                to={{
                  pathname: `/quiz/${encodeURIComponent(
                    tenseData.title || "unknown"
                  )}`,
                  state: {
                    course_id: tenseData.tense_id,
                    course_title: tenseData.title,
                  },
                }}
                className="px-4 py-2 rounded-lg font-medium transition-colors bg-white/20 text-white hover:bg-white hover:text-orange-600"
                onClick={() => {
                  console.log("Take Quiz clicked - tenseData:", tenseData);
                  console.log(
                    "Quiz URL will be:",
                    `/quiz/${encodeURIComponent(tenseData.title || "unknown")}`
                  );
                }}
              >
                <PencilSquareIcon className="w-5 h-5 inline mr-1" /> Take Quiz
              </Link>
            </div> */}
          {/* </div> */}

          {/* Course Stats */}
          {/* <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4 mt-10"
          >
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center text-white">
              <AcademicCapIcon className="w-8 h-8 mx-auto mb-2" />
              <h4 className="text-xs uppercase tracking-wider font-semibold">
                Completion
              </h4>
              <p className="text-2xl font-bold">
                {courseStats.completionRate}%
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center text-white">
              <ChartBarIcon className="w-8 h-8 mx-auto mb-2" />
              <h4 className="text-xs uppercase tracking-wider font-semibold">
                Avg. Score
              </h4>
              <p className="text-2xl font-bold">{courseStats.avgScore}%</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center text-white">
              <ClockIcon className="w-8 h-8 mx-auto mb-2" />
              <h4 className="text-xs uppercase tracking-wider font-semibold">
                Study Time
              </h4>
              <p className="text-2xl font-bold">{courseStats.studyTime}h</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center text-white">
              <DocumentTextIcon className="w-8 h-8 mx-auto mb-2" />
              <h4 className="text-xs uppercase tracking-wider font-semibold">
                Examples
              </h4>
              <p className="text-2xl font-bold">
                {courseStats.totalExamples || 0}
              </p>
            </div>
          </motion.div> */}
        {/* </motion.div>
      </Section> */}

      {/* Main Content */}
      <div className="mx-auto max-w-5xl px-4 pt-8 sm:px-6">
        <div className="mt-3 flex flex-wrap items-start justify-between gap-x-8 gap-y-4">
          <div className="min-w-0">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {tenseData?.title}
            </h1>
            <p className="mt-2 max-w-2xl text-slate-600">
              See a real sentence, understand it, write your own, then take the
              quiz.
            </p>
            {courseStats.completionRate > 0 && (
              <p className="mt-3 text-sm text-slate-500">
                You have covered {courseStats.completionRate}% of this course.
              </p>
            )}
          </div>

          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:border-orange-300 hover:text-orange-700"
          >
            Back to the tense map
          </Link>
        </div>

        <div className="mt-6">
          <SpeechFeature ref={speechFeatureRef} text={lessonText} />
        </div>


        <section id="intro" className="mt-8 scroll-mt-28">
          <StageHeading
            number={1}
            title="See it"
            hint="One sentence to start with, in its real form."
          />
          <div className="mt-4">
            <TenseIntro examples={tenseData?.examples} />
          </div>
        </section>

        <div className="mt-10">
          {/* Course Content Section */}
          <StageHeading
            number={2}
            title="Understand it"
            hint="What it means and how to use it."
          />
          <motion.div
            ref={contentRef}
            id="explain"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-12 mt-4 scroll-mt-28"
          >
            <Card className="overflow-hidden shadow-lg border border-slate-200">
              <div className="bg-slate-800 px-6 py-4">
                <h2 className="text-xl font-bold text-white flex items-center">
                  <BookOpenIcon className="w-5 h-5 mr-2" /> How it works
                </h2>
              </div>
              <div className="p-6 md:p-8">
                <div className="max-w-[68ch] space-y-4 text-[1.0625rem] leading-8 text-slate-700">
                  {(lesson.explanation.length
                    ? lesson.explanation
                    : [decodeEntities(tenseData.description)]
                  ).map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}

                  {lesson.examples.length > 0 && (
                    <div className="!mt-8">
                      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                        Examples
                      </h2>
                      <ul className="mt-3 space-y-2">
                        {lesson.examples.map((example, index) => (
                          <li
                            key={index}
                            className="rounded-lg border-l-2 border-orange-400 bg-slate-50 px-4 py-3 text-slate-800"
                          >
                            {example}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* some courses carry a second, differently shaped body of text;
                      when it is the same string as the description, showing it again
                      just reads as a mistake */}
                  {hasDistinctContent && (
                  <div className="prose max-w-none">
                    {tenseData?.content.split("\n").map((part, index) => {
                      const trimmed = part.trim().toLowerCase();

                      const isHeading = [
                        "affirmative",
                        "negative",
                        "interrogative",
                      ].some((keyword) => trimmed.startsWith(keyword));

                      return isHeading ? (
                        <h3
                          key={index}
                          className="text-lg font-semibold text-orange-600 mt-6"
                        >
                          {part}
                        </h3>
                      ) : (
                        <p
                          key={index}
                          className="mb-4 text-gray-700 leading-relaxed"
                        >
                          {part}
                        </p>
                      );
                    })}
                  </div>
                  )}
                </div>
              </div>
            </Card>

            {/* Examples Section */}
            <StageHeading
              number={3}
              title="Write your own"
              hint="Add a sentence, and read what other students wrote."
            />
            <motion.div
              ref={examplesRef}
              id="practice"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mb-12 mt-4 scroll-mt-28"
            >
              <div className="flex mt-8 flex-col gap-6">
                {/* Examples Section - Now on Top */}
                <div className="w-full">
                  <Card className="shadow-lg border border-slate-200 h-full">
                    <div className="bg-slate-800 px-6 py-4 flex justify-between items-center">
                      <h2 className="text-xl font-bold text-white">
                        Examples from students
                      </h2>
                      {tenseData.examples && tenseData?.examples.length > 0 && (
                        <button
                          onClick={speakExamples}
                          className="p-1.5 text-gray-200 hover:text-white hover:bg-slate-700 rounded-full transition-colors duration-200"
                          title="Listen to your examples"
                        >
                          <SpeakerWaveIcon className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                    <div className="p-6 h-full flex flex-col">
                      {tenseData.examples && tenseData.examples.length > 0 ? (
                        <motion.div
                          variants={containerVariants}
                          initial="hidden"
                          animate="visible"
                          className="space-y-3 flex-grow overflow-y-auto pr-1 max-h-[46vh]"
                        >
                          {[...tenseData.examples]
                            // this thread is what students wrote; the lesson's own
                            // authored examples are shown in the card above
                            .filter((example) => example.user)
                            .filter((example) =>
                              looksLikeASentence(example.example_text)
                            )
                            .reverse()
                            .map((example, index) => (
                            <motion.div
                              key={index}
                              variants={itemVariants}
                              className="flex items-start gap-2.5"
                            >
                              <div className="h-8 w-8 flex-shrink-0 rounded-full bg-orange-100 text-orange-700 text-sm font-semibold flex items-center justify-center">
                                {String(example.user?.user_name || "G")
                                  .charAt(0)
                                  .toUpperCase()}
                              </div>
                              <div className="min-w-0 max-w-[85%] rounded-2xl rounded-tl-sm border border-slate-200 bg-white px-4 py-2.5 shadow-sm">
                                <div className="mb-1 flex items-center gap-2">
                                  <span className="text-xs font-semibold text-slate-600 truncate">
                                    {example.user?.user_name || "Guest"}
                                  </span>
                                  <span
                                    className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full ${
                                      example.sentence_type === "affirmative"
                                        ? "bg-green-200 text-green-800"
                                        : example.sentence_type === "negative"
                                        ? "bg-red-200 text-red-800"
                                        : "bg-blue-200 text-blue-800"
                                    }`}
                                  >
                                    {example.sentence_type}
                                  </span>
                                  {example.created_at && (
                                    <span className="text-[10px] text-slate-400">
                                      {new Date(example.created_at).toLocaleDateString()}
                                    </span>
                                  )}
                                </div>
                                <p className="text-gray-800 leading-snug break-words">
                                  {example.example_text}
                                </p>
                              </div>
                            </motion.div>
                          ))}
                        </motion.div>
                      ) : (
                        <div className="text-center py-8 text-gray-500 flex-grow flex flex-col justify-center">
                          <DocumentTextIcon className="w-12 h-12 mx-auto opacity-30 mb-3" />
                          <p>No examples added yet.</p>
                          <p className="text-sm">
                            Add your own examples to enhance your learning!
                          </p>
                        </div>
                      )}

                      
                    </div>
                                    <div>
                  <Card className="shadow-lg overflow-hidden border border-slate-200 h-full">
                    
                    <div className="p-1">
                        <SentencePracticeChat
                          tenseId={tenseData?.tense_id}
                          tenseName={tenseData?.title || ""}
                          onSaved={() => {
                            setUploadSuccess(true);
                            fetchExamples();
                          }}
                        />
                    </div>
                  </Card>
                </div>
                  </Card>
                </div>

                <section id="quiz" className="mt-10 scroll-mt-28">
                  <StageHeading
                    number={4}
                    title="Take the quiz"
                    hint="A short quiz on this lesson, marked as you go."
                  />
                  <Link
                    to={{
                      pathname: `/quiz/${encodeURIComponent(
                        tenseData.title || "unknown"
                      )}`,
                      state: {
                        course_id: tenseData.tense_id,
                        course_title: tenseData.title,
                      },
                    }}
                    className="mt-4 inline-flex items-center gap-2 rounded-lg bg-orange-500 px-5 py-3 font-semibold text-white shadow-sm transition-colors hover:bg-orange-600"
                  >
                    <PlayIcon className="h-5 w-5" />
                    Start the quiz
                  </Link>
                </section>

                {(lessonNav.previous || lessonNav.next) && (
                  <nav className="mt-12 flex items-center gap-4 border-t border-slate-200 pt-5 text-sm">
                    {lessonNav.previous && (
                      <Link
                        to={`/course/${encodeURIComponent(lessonNav.previous.title)}`}
                        className="group inline-flex max-w-[45%] items-center gap-2.5 text-left"
                      >
                        <ArrowLeftIcon className="h-4 w-4 shrink-0 text-slate-300 transition-all duration-200 group-hover:-translate-x-1 group-hover:text-orange-500" />
                        <span className="min-w-0">
                          <span className="block text-xs uppercase tracking-wide text-slate-400">
                            Previous
                          </span>
                          <span className="block truncate font-medium text-slate-600 transition-colors group-hover:text-orange-600">
                            {lessonNav.previous.title}
                          </span>
                        </span>
                      </Link>
                    )}
                    {lessonNav.next && (
                      <Link
                        to={`/course/${encodeURIComponent(lessonNav.next.title)}`}
                        className="group ml-auto inline-flex max-w-[45%] items-center gap-2.5 text-right"
                      >
                        <span className="min-w-0">
                          <span className="block text-xs uppercase tracking-wide text-slate-400">
                            Next
                          </span>
                          <span className="block truncate font-medium text-slate-600 transition-colors group-hover:text-orange-600">
                            {lessonNav.next.title}
                          </span>
                        </span>
                        <ArrowRightIcon className="h-4 w-4 shrink-0 text-slate-300 transition-all duration-200 group-hover:translate-x-1 group-hover:text-orange-500" />
                      </Link>
                    )}
                  </nav>
                )}

                {/* Upload Form - Now on Bottom */}

              </div>
            </motion.div>

            {/* Related Materials */}
            {/* <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card
                className={`p-5 transition-shadow duration-200 border ${
                  showReferences
                    ? "border-orange-300 shadow-md"
                    : "border-slate-200 hover:shadow-lg"
                }`}
              >
                <h3 className="font-bold text-gray-800 mb-2 flex items-center">
                  <BookOpenIcon className="w-5 h-5 mr-2 text-orange-500" />
                  Grammar References
                </h3>

                <p className="text-sm text-gray-600 mb-3">
                  Access comprehensive grammar guides and examples related to
                  this course.
                </p>

                <button
                  onClick={() => setShowReferences(!showReferences)}
                  className={`text-sm font-medium inline-flex items-center transition-colors duration-200 ${
                    showReferences
                      ? "text-orange-700"
                      : "text-orange-600 hover:text-orange-700"
                  }`}
                >
                  {showReferences ? "Hide References" : "View References"}
                  <ArrowRightIcon
                    className={`w-4 h-4 ml-1 transform transition-transform duration-200 ${
                      showReferences ? "rotate-90" : ""
                    }`}
                  />
                </button>

                {showReferences && (
                  <div className="mt-4 border-t pt-3 text-sm text-gray-700 whitespace-pre-line">
                    {tenseData.grammer_references}
                  </div>
                )}
              </Card>

              <Card className="p-5 hover:shadow-lg transition-shadow duration-200 border border-slate-200">
                <h3 className="font-bold text-gray-800 mb-2 flex items-center">
                  <PencilSquareIcon className="w-5 h-5 mr-2 text-orange-500" />
                  Practice Exercises
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  Additional exercises to help reinforce the concepts from this
                  course.
                </p>
                <Link
                  to={`/tense/${tenseData?.tense_id}/practice`}
                  className="text-orange-600 hover:text-orange-700 text-sm font-medium inline-flex items-center"
                >
                  Start Practice <ArrowRightIcon className="w-4 h-4 ml-1" />
                </Link>
              </Card>
            </div> */}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PageCourse;
